<template>
    <div>
        <Head :title="__(title)" />
        <div class="flex flex-wrap">
            <div class="max-w-full lg:w-3/5">
                <form @submit.prevent="update" class="bg-white rounded-md shadow overflow-hidden mr-2">
                    <!-- Closed ticket banner -->
                    <div v-if="ticket.closed" class="bg-red-100 text-red-800 p-3 px-8 border-b border-red-200 flex items-center">
                        <icon name="lock-closed" class="w-5 h-5 mr-2" />
                        <span>
                            {{ __('This ticket was closed') }}
                            <span v-if="ticket.closed_at">
                                {{ __('on') }} {{ moment(ticket.closed_at).format('LLL') }}
                            </span>
                            <span v-if="ticket.closed_by">
                                {{ __('by') }} {{ ticket.closed_by_user?.name || __('System') }}
                            </span>
                        </span>
                    </div>

                    <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
                        <!-- Customer field (only editable by non-customers) -->
                        <select-edit-input
                            v-if="auth.user.role.slug !== 'customer'"
                            placeholder="Search customer"
                            :on-input="doFilter"
                            :items="customers"
                            v-model="form.user_id"
                            :error="form.errors.user_id"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Customer')"
                            :value="ticket.user"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Priority field -->
                        <select-edit-input
                            placeholder="Search priority"
                            :items="priorities"
                            v-model="form.priority_id"
                            :error="form.errors.priority_id"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Priority')"
                            :value="ticket.priority"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Assigned to field -->
                        <select-edit-input
                            v-if="auth.user.role.slug !== 'customer' && !(hidden_fields && hidden_fields.includes('assigned_to'))"
                            placeholder="Search user"
                            :on-input="doFilterUsersExceptCustomer"
                            :items="usersExceptCustomers"
                            v-model="form.assigned_to"
                            :error="form.errors.assigned_to"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Assigned to')"
                            :value="ticket.assigned_user ?? 'Not Assigned'"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Status field -->
                        <select-edit-input
                            placeholder="Select status to change"
                            :items="statuses"
                            v-model="form.status_id"
                            :error="form.errors.status_id"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Status')"
                            :value="ticket.status ? ticket.status.name : 'N/A'"
                            :editable="auth.user.role.slug !== 'customer' && user_access.ticket.update && !ticket.closed">
                            <template #selected-option>
                                <div class="flex items-center">
                                    <span class="inline-block w-3 h-3 rounded-full mr-2" :class="{
                                        'bg-green-500': ticket.status?.slug.includes('open') || ticket.status?.slug.includes('active'),
                                        'bg-yellow-500': ticket.status?.slug.includes('pending') || ticket.status?.slug.includes('hold'),
                                        'bg-red-500': ticket.status?.slug.includes('closed') || ticket.status?.slug.includes('rejected'),
                                        'bg-blue-500': ticket.status?.slug.includes('new') || ticket.status?.slug.includes('processing'),
                                        'bg-gray-500': !ticket.status || !['open', 'active', 'pending', 'hold', 'closed', 'rejected', 'new', 'processing'].some(s => ticket.status.slug.includes(s))
                                    }"></span>
                                    {{ ticket.status ? ticket.status.name : 'N/A' }}
                                </div>
                            </template>
                            <template #option="{ item }">
                                <div class="flex items-center">
                                    <span class="inline-block w-3 h-3 rounded-full mr-2" :class="{
                                        'bg-green-500': item.slug.includes('open') || item.slug.includes('active'),
                                        'bg-yellow-500': item.slug.includes('pending') || item.slug.includes('hold'),
                                        'bg-red-500': item.slug.includes('closed') || item.slug.includes('rejected'),
                                        'bg-blue-500': item.slug.includes('new') || item.slug.includes('processing'),
                                        'bg-gray-500': !['open', 'active', 'pending', 'hold', 'closed', 'rejected', 'new', 'processing'].some(s => item.slug.includes(s))
                                    }">
                                    </span>
                                    {{ item.name }}
                                </div>
                            </template>
                        </select-edit-input>

                        <!-- Ticket type field -->
                        <select-edit-input
                            v-if="!(hidden_fields && hidden_fields.includes('ticket_type'))"
                            placeholder="Search type"
                            :items="types"
                            v-model="form.type_id"
                            :error="form.errors.type_id"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Ticket type')"
                            :value="ticket.type"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Department field -->
                        <select-edit-input
                            v-if="!(hidden_fields && hidden_fields.includes('department'))"
                            @change="getCategories()"
                            placeholder="Search department"
                            :items="departments"
                            v-model="form.department_id"
                            :error="form.errors.department_id"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Department')"
                            :value="ticket.department"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Category field -->
                        <select-edit-input
                            ref="category"
                            v-if="!(hidden_fields && hidden_fields.includes('category')) && form.department_id"
                            @change="getSubCategories"
                            placeholder="Search category"
                            :items="categories"
                            v-model="form.category_id"
                            :error="form.errors.category_id"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Category')"
                            :editable="user_access.ticket.update && !ticket.closed" />

                        <!-- Sub-category field -->
                        <select-edit-input
                            ref="sub_category"
                            v-if="!(hidden_fields && hidden_fields.includes('category')) && form.category_id"
                            placeholder="Search sub-category"
                            :items="sub_categories"
                            v-model="form.sub_category_id"
                            :error="form.errors.sub_category_id"
                            class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Sub Category')"
                            :editable="user_access.ticket.update && !ticket.closed" />

                        <!-- Created date (readonly) -->
                        <div class="assigned_user pr-6 pb-8 w-full lg:w-1/3 flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Created') }} </div>
                            <div class="font-light text-sm">{{ moment(ticket.created_at).fromNow() }}</div>
                        </div>

                        <!-- Channel used (readonly) -->
                        <div class="assigned_user pr-6 pb-8 w-full lg:w-1/3 flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Channel Used') }} </div>
                            <div class="font-light text-sm">{{ ticket.source }}</div>
                        </div>

                        <!-- Subject field (readonly) -->
                        <div class="pr-6 pb-8 w-full lg:w-2/3 flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Subject') }}</div>
                            <div class="font-light text-sm">{{ ticket.subject }}</div>
                        </div>

                        <!-- Request Details (readonly) -->
                        <div class="assigned_user pr-6 pb-8 w-full lg:w-full flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Request Details') }}</div>
                            <div class="font-light text-sm" v-html="ticket.details"></div>
                        </div>

                        <!-- File Attachments (readonly) -->
                        <div class="pr-6 pb-8 w-full lg:w-full flex-col">
                            <div v-if="attachments.length" class="flex items-center justify-between pr-6 pt-8 w-full"
                                v-for="(file, fi) in attachments" :key="fi">
                                <div class="flex-1 pr-1">
                                    {{ file.name }} <span class="text-gray-500 text-xs">({{ getFileSize(file.size) }})</span>
                                    <a v-if="file.user" class="text-sm"
                                        :href="this.route('users.edit', file.user.id)">{{ file.user.first_name }} {{ file.user.last_name }}</a>
                                    at <span class="text-sm">{{ file.created_at }}</span>
                                </div>
                                <div class="a__buttons flex justify-end items-center">
                                    <button type="button" class="btn flex items-center"
                                        @click="downloadAttachment(file)">
                                        {{ __('Download') }}</button>
                                </div>
                            </div>
                        </div>

                        <!-- Rating section (only for customers on closed tickets) -->
                        <div class="flex flex-col w-full"
                            v-if="ticket.status && ticket.closed && !ticket.review && auth.user.role.slug === 'customer'">
                            <div class="assigned_user star__review pb-5 w-full lg:w-1/3 flex flex-col">
                                <div class="font-bold">{{ __('How do you rate this support service?') }} </div>
                                <div class="star-rating pt-5">
                                    <input type="radio" id="5-stars" name="rating" v-model="form.rating" value="5" />
                                    <label for="5-stars" class="star">&#9733;</label>
                                    <input type="radio" id="4-stars" name="rating" v-model="form.rating" value="4" />
                                    <label for="4-stars" class="star">&#9733;</label>
                                    <input type="radio" id="3-stars" name="rating" v-model="form.rating" value="3" />
                                    <label for="3-stars" class="star">&#9733;</label>
                                    <input type="radio" id="2-stars" name="rating" v-model="form.rating" value="2" />
                                    <label for="2-stars" class="star">&#9733;</label>
                                    <input type="radio" id="1-star" name="rating" v-model="form.rating" value="1" />
                                    <label for="1-star" class="star">&#9733;</label>
                                </div>
                            </div>
                            <textarea-input v-model="form.review" :error="form.errors.review"
                                class="pr-6 pb-4 w-full lg:w-1/3" :label="__('Feedback')" />
                            <div class="flex lg:w-1/4 mb-4"><button class="btn-indigo" type="submit">{{ __('Submit') }}</button></div>
                        </div>
                    </div>
                    <div class="px-8 py-4 bg-gray-50 border-t border-gray-100 flex items-center">
                        <button v-if="user_access.ticket.delete" class="text-red-600 hover:underline" tabindex="-1"
                            type="button" @click="destroy">
                            {{ __('Delete') }}</button>
                        <Link :href="route('tickets.email.create', ticket.id)" class="btn-indigo ml-2">
                            {{ __('Send Email') }}
                        </Link>

                        <!-- Close Ticket button -->
                        <button
                            v-if="!ticket.closed && user_access.ticket.update"
                            class="text-red-600 hover:underline ml-2"
                            @click="openCloseTicketModal">
                            <icon name="lock-closed" class="w-4 h-4 mr-1 inline" />
                            {{ __('Close Ticket') }}
                        </button>

                        <!-- Reopen Ticket button -->
                        <button
                            v-if="ticket.closed && user_access.ticket.update"
                            class="text-green-600 hover:underline ml-2"
                            @click="openReopenModal">
                            <icon name="lock-open" class="w-4 h-4 mr-1 inline" />
                            {{ __('Reopen Ticket') }}
                        </button>

                        <Link :href="route('tickets.audit', ticket.id)" class="text-blue-600 hover:underline ml-2">
                            {{ __('View Audit Trail') }}
                        </Link>
                        <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
                            {{ __('Save') }}
                        </loading-button>
                    </div>
                </form>
            </div>

            <!-- Chat area remains unchanged -->
             <div class="max-w-full lg:w-2/5">
                <div class="bg-white rounded-md shadow overflow-hidden ml-2 chat-area comment-box flex-1 flex flex-col">
                    <div class="flex-3">
                        <div class="chat-header flex flex-col pb-3">
                            <h3 class="text-xl">{{ __('Ticket discussion') }}</h3>
                            <p class="text-sm font-light">{{ __('Comment histories for this ticket will be available here.') }}</p>
                        </div>
                    </div>
                    <div class="messages flex-1 overflow-auto reverse__order">
                        <div v-for="(comment, index) in comments.slice().reverse()" :key="index"
                            class="message mb-4 flex">
                            <div v-if="comment.user_id !== user.id" class="flex-2">
                                <div class="w-12 h-12 relative">
                                    <span class="w-12 h-12 rounded-full mx-auto user_icon" alt="chat-user"
                                        v-if="comment.user">
                                        {{ Array.from(comment.user.first_name)[0] }}</span>
                                </div>
                            </div>
                            <div v-if="comment.user_id !== user.id" class="flex-1 px-2 mt__wrap">
                                <h3 class="font-bold pb-2 text-sm pt-1" v-if="comment.user">{{ comment.user.first_name
                                    }} {{
                                    comment.user.last_name }}</h3>
                                <div
                                    class="inline-block bg-gray-300 user-comment-round p-2 px-3 text-gray-700 leading-5">
                                    <span>{{ stripHtml(comment.details) }}</span>
                                </div>
                                <div class="pl-4"><small class="text-gray-500">{{
                                    moment(comment.updated_at).fromNow(true)
                                        }}</small></div>
                            </div>

                            <div v-if="comment.user_id === user.id" class="flex-1 mt__wrap px-2 text-right">
                                <div class="inline-block bg-blue rounded p-2 px-3 text-white leading-5"
                                    v-html="comment.details">
                                </div>
                                <div class="pr-4"><small class="text-gray-500">{{
                                    moment(comment.updated_at).fromNow(true)
                                        }}</small></div>
                            </div>
                        </div>
                    </div>
                    <div class="flex-2 pt-4 pb-3">
                        <div class="write bg-white shadow flex rounded-lg">
                            <div class="flex-1 ticket_comment_edit">
                                <ckeditor id="ticketDiscussion" :editor="editor" v-model="comment" name="comment"
                                    :config="editorConfig" autofocus></ckeditor>
                            </div>
                            <div class="flex-2 w-35 p-2 flex content-center items-center">
                                <div class="flex-1">
                                    <button class="bg-blue w-10 h-10 rounded-full flex justify-center items-center"
                                        @click="submitComment">
                                        <icon class="w-4 h-4 fill-gray-100" name="send" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reopen Ticket Modal -->
<Modal :show="showReopenModal" @close="closeReopenModal" max-width="md">
    <div class="p-6">
        <h2 class="text-lg font-medium text-gray-900 mb-4">
            {{ __('Reopen Ticket') }} #{{ ticket.uid }}
        </h2>

        <select-input
            v-model="reopenForm.status_id"
            :error="reopenForm.errors.status_id"
            class="pb-4 w-full"
            :label="__('New Status *')"
            required
        >
            <option v-for="status in reopenStatuses" :key="status.id" :value="status.id">
                {{ status.name }}
            </option>
        </select-input>

        <textarea-input
            v-model="reopenForm.reason"
            :error="reopenForm.errors.reason"
            class="pb-4 w-full"
            :label="__('Reason for Reopening *')"
            required
            rows="5"
        />

        <div class="flex justify-end mt-6 space-x-2">
            <button type="button" @click="closeReopenModal" class="btn-gray">
                {{ __('Cancel') }}
            </button>
            <LoadingButton
                @click="submitReopen"
                :loading="reopenForm.processing"
                class="btn-green"
            >
                {{ reopenForm.processing ? __('Reopening...') : __('Reopen Ticket') }}
            </LoadingButton>
        </div>
    </div>
</Modal>

            <!-- Add this modal at the bottom of your template (before closing </div>) -->
<Modal :show="showCloseTicketModal" @close="closeCloseTicketModal" max-width="md">
    <div class="p-6">
        <h2 class="text-lg font-medium text-gray-900 mb-4">
            {{ __('Close Ticket') }}
        </h2>

        <textarea-input
            v-model="closeForm.resolution"
            :error="closeForm.errors.resolution"
            class="pb-4 w-full"
            :label="__('Resolution Details *')"
            required
            rows="5"
        />

        <div class="flex justify-end mt-6 space-x-2">
            <button type="button" @click="closeCloseTicketModal" class="btn-gray">
                {{ __('Cancel') }}
            </button>
            <LoadingButton
                @click="closeTicket"
                :loading="closeForm.processing"
                class="btn-red"
            >
                {{ closeForm.processing ? __('Closing...') : __('Close Ticket') }}
            </LoadingButton>
        </div>
    </div>
</Modal>
    </div>
</template>

<script>
import { Link, Head } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Modal from '@/Shared/Modal'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import moment from 'moment'
import TextEditInput from '@/Shared/TextEditInput'
import SelectInput from '@/Shared/SelectInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import SelectEditInput from '@/Shared/SelectEditInput'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UploadAdapter from '@/Shared/UploadAdapter';
import axios from 'axios'
import CKEditor from '@ckeditor/ckeditor5-vue';

export default {
    components: {
        LoadingButton,
        SelectInput,
        TextInput,
        TextEditInput,
        TextareaInput,
        Link,
        Head,
        Icon,
        SelectInputFilter,
        SelectEditInput,
        ckeditor: CKEditor.component,
        Modal,
    },
    layout: Layout,
    props: {
        title: String,
        ticket: Object,
        priorities: Array,
        statuses: Array,
        types: Array,
        departments: Array,
        all_categories: { required: false },
        customers: Array,
        usersExceptCustomers: Array,
        attachments: Array,
        comments: Array,
        auth: Object,
        hidden_fields: Object,
    },
    data() {
        return {
            user: this.$page.props.auth.user,
            type_status: [],
            categories: this.all_categories.filter(cat => cat.department_id === this.ticket.department_id),
            sub_categories: this.all_categories.filter(cat => cat.parent_id === this.ticket.category_id),
            comment: '',
            source: '',
            editCustomer: false,
            enableEditor: false,
            user_access: this.$page.props.auth.user.access,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: ['heading', '|', 'bold', 'italic', 'link', '|', 'undo', 'redo', '|', 'bulletedList', 'numberedList', '|', 'outdent', 'indent', '|', 'insertTable', 'blockQuote', '|', 'imageUpload', 'mediaEmbed', '|'],
                table: {
                    toolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
                },
                extraPlugins: [this.uploader],
            },
            showCloseTicketModal: false,
            showReopenModal: false,
            reopenForm: this.$inertia.form({
                status_id: null,
                reason: '',
            }),
            reopenStatuses: this.statuses.filter(status => !status.slug.includes('closed')),
          closeForm: this.$inertia.form({
                resolution: '',
                root_cause_id: null,
                resolution_id: null,
            }),
            form: this.$inertia.form({
                user_id: this.ticket.user_id,
                priority_id: this.ticket.priority_id,
                status_id: this.ticket.status_id,
                department_id: this.ticket.department_id,
                category_id: this.ticket.category_id ?? null,
                sub_category_id: this.ticket.sub_category_id,
                assigned_to: this.ticket.assigned_to,
                type_id: this.ticket.type_id,
                rating: 0,
                review: '',
            }),
        };
    },
    created() {
        if (this.auth.user.role.slug === 'customer' && this.statuses.length) {
            this.type_status = this.statuses.filter(status => (status.id === this.form.status_id) || status.name.match(/Close.*/))
        } else {
            this.type_status = this.statuses
        }
    },
    methods: {
        moment(date) {
            return moment(date);
        },
        getCategories() {
            this.ticket.category = 'N/A';
            this.form.category_id = null;
            this.categories = this.all_categories.filter(cat => cat.department_id === this.form.department_id)
        },
        stripHtml(html) {
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || "";
        },
        getSubCategories() {
            this.sub_categories = this.all_categories.filter(cat => cat.parent_id === this.form.category_id)
            this.ticket.sub_category = 'N/A';
            this.form.sub_category_id = null;
        },
        uploader(editor) {
            editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                return new UploadAdapter(loader);
            };
        },
        doFilter(e) {
            axios.get(this.route('filter.customers', { search: e.target.value })).then((res) => {
                this.customers.splice(0, this.customers.length, ...res.data);
            })
        },
        doFilterUsersExceptCustomer(e) {
            axios.get(this.route('filter.users_except_customer', { search: e.target.value })).then((res) => {
                this.usersExceptCustomers.splice(0, this.usersExceptCustomers.length, ...res.data);
            })
        },
        getFileSize(size) {
            const i = Math.floor(Math.log(size) / Math.log(1024))
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'KB', 'MB', 'GB', 'TB'][i]
        },
        downloadAttachment(file) {
            const link = document.createElement("a");
            link.href = window.location.origin + '/files/' + file.path;
            link.download = file.name;
            link.click();
        },
        update() {
            this.form.post(this.route('tickets.update', this.ticket.id))
        },
        destroy() {
            if (confirm('Are you sure you want to delete this ticket?')) {
                this.$inertia.delete(this.route('tickets.destroy', this.ticket.id))
            }
        },
        openCloseTicketModal() {
            this.showCloseTicketModal = true;
        },
        closeCloseTicketModal() {
            this.showCloseTicketModal = false;
            this.closeForm.reset();
            this.closeForm.errors = {};
        },
closeTicket() {
    this.closeForm.post(this.route('tickets.close', this.ticket.id), {
        preserveScroll: true,
        onSuccess: () => {
            if(confirm('Would you like to send the resolution details to the customer?')) {
                this.sendResolutionEmail();
            }
            this.closeCloseTicketModal();
            this.$inertia.reload({ only: ['ticket'] });
        }
    });
},
sendResolutionEmail() {
    axios.post(this.route('tickets.email.resolution', this.ticket.id), {
        resolution: this.closeForm.resolution
    }).then(response => {
        this.$toast.success('Resolution email sent successfully');
    }).catch(error => {
        this.$toast.error('Failed to send resolution email');
    });
},

        openReopenModal() {
            const openStatus = this.statuses.find(s => !s.slug.includes('closed'));
            this.reopenForm.status_id = openStatus?.id || null;
            this.showReopenModal = true;
        },
        closeReopenModal() {
            this.showReopenModal = false;
            this.reopenForm.reset();
            this.reopenForm.errors = {};
        },
        submitReopen() {
            this.reopenForm.post(this.route('tickets.reopen', this.ticket.id), {
                preserveScroll: true,
                onSuccess: () => {
                    this.closeReopenModal();
                    this.$toast.success('Ticket reopened successfully');
                    this.$inertia.reload({ only: ['ticket'] });
                },
                onError: (errors) => {
                    this.$toast.error('Failed to reopen ticket');
                    if (errors.reason) {
                        this.$toast.error(errors.reason);
                    }
                }
            });
        },
        submitComment() {
            const messageData = {
                comment: this.comment,
                user_id: this.user.id,
                _token: this.$page.props.csrf_token,
                ticket_id: this.ticket.id
            }
            this.comment = ''
            axios.post(this.route('ticket.comment'), messageData).then((response) => {
                if (response.data) {
                    this.comments.push(response.data)
                }
                this.$emit('comment submitted!')
            }).catch((error) => {
                console.log(error)
            })
        },
    }
};
</script>
